import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5o0bj7ecauet1j8batg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <h2 className="font-['Playfair_Display',serif] text-[48px] font-bold text-[#1A2B3C] mb-6">
              (Get in Touch)
            </h2>
            <p className="text-[16px] text-gray-700 leading-relaxed mb-8">
              Ready to take your business to the next level? Let's discuss how we can help you achieve your goals with government contracting, business formation, compliance, and more.
            </p>
            
            <div className="space-y-6 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1A2B3C] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-[#D4AF37] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[17px] text-[#1A2B3C] mb-2">Business Hours</h3>
                  <p className="text-[15px] text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p className="text-[15px] text-gray-600">Saturday - Sunday: Closed</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1A2B3C] rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-[#D4AF37] text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-[17px] text-[#1A2B3C] mb-2">Service Areas</h3>
                  <p className="text-[15px] text-gray-600">United States & United Kingdom</p>
                </div>
              </div>
            </div>

            <div className="bg-[#FAFAF8] rounded-2xl p-8">
              <h3 className="font-bold text-[20px] text-[#1A2B3C] mb-4">Why Choose Us?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                  <span className="text-[15px] text-gray-700">15+ years of industry experience</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                  <span className="text-[15px] text-gray-700">Expert guidance for US & UK markets</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                  <span className="text-[15px] text-gray-700">500+ successful client projects</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#D4AF37] text-xl mt-0.5"></i>
                  <span className="text-[15px] text-gray-700">Personalized, responsive support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mt-16 pt-12 border-t border-gray-200">
          <div className="text-center">
            <p className="text-gray-600 text-[14px] mb-6">
              © 2024 Business Contracting Hub. All rights reserved.
            </p>
            
            <div className="flex items-center justify-center gap-6 mb-6">
              <a href="#" className="text-gray-500 text-[14px] hover:text-[#1A2B3C] transition-colors cursor-pointer">Privacy Policy</a>
              <span className="text-gray-400">|</span>
              <a href="#" className="text-gray-500 text-[14px] hover:text-[#1A2B3C] transition-colors cursor-pointer">Terms of Service</a>
              <span className="text-gray-400">|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 text-[14px] hover:text-[#1A2B3C] transition-colors cursor-pointer">Website Builder</a>
            </div>

            <div className="flex justify-center gap-3">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all cursor-pointer">
                <i className="ri-linkedin-fill text-gray-600 text-lg hover:text-white"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all cursor-pointer">
                <i className="ri-twitter-x-line text-gray-600 text-lg hover:text-white"></i>
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all cursor-pointer">
                <i className="ri-facebook-fill text-gray-600 text-lg hover:text-white"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center hover:bg-[#D4AF37] hover:border-[#D4AF37] transition-all cursor-pointer">
                <i className="ri-instagram-line text-gray-600 text-lg hover:text-white"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
